import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { forwardRef } from 'react';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const TextareaWrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.textDark};
  text-align: right;
`;

const StyledTextarea = styled.textarea<{ hasError?: boolean }>`
  padding: ${theme.spacing.md};
  font-size: ${theme.fontSizes.md};
  border: 2px solid ${({ hasError }) => hasError ? theme.colors.error : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.background};
  color: ${theme.colors.text};
  transition: all ${theme.transitions.fast};
  direction: rtl;
  text-align: right;
  font-family: ${theme.fonts.primary};
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${theme.colors.textLight};
    text-align: right;
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ hasError }) => 
      hasError ? 'rgba(231, 76, 60, 0.1)' : 'rgba(255, 107, 157, 0.1)'};
  }

  &:disabled {
    background-color: ${theme.colors.backgroundDark};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ErrorText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.error};
  text-align: right;
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  text-align: right;
`;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, fullWidth, ...props }, ref) => {
    return (
      <TextareaWrapper fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <StyledTextarea ref={ref} hasError={!!error} {...props} />
        {error && <ErrorText>{error}</ErrorText>}
        {!error && helperText && <HelperText>{helperText}</HelperText>}
      </TextareaWrapper>
    );
  }
);

Textarea.displayName = 'Textarea';
